import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveToken, changePassword, getMe, updateMe, getTokens, deleteToken, API_URL } from '../services/api';
import BrandVoiceSettings from './BrandVoice/BrandVoiceSettings';
import {
    Settings as SettingsIcon, Key, LogOut,
    Edit2, Trash2, ChevronLeft, AlertTriangle,
    X, Palette, Shield, Network, Users, Lock, ChevronDown, RefreshCw
} from 'lucide-react';

// Consistent color palette
const colors = {
    primary: '#4285f4', // Brand Blue
    primaryLight: '#8ab4f8',
    primaryDark: '#1a73e8',
    success: '#34a853', // Brand Green
    successLight: '#e6f4ea',
    warning: '#fbbc04', // Brand Yellow
    warningLight: '#fef7e0',
    danger: '#ea4335', // Brand Red
    dangerLight: '#fce8e6',
    purple: '#4285f4', // Replaced purple with Brand Blue
    purpleLight: '#e8f0fe', // Replaced purpleLight with Blue Light
    blue: '#4285f4',
    blueLight: '#e8f0fe',
    gray: '#5f6368',
    grayLight: '#f1f3f4',
    border: '#dadce0',
    text: '#202124',
    textSecondary: '#5f6368'
};

// Platform Icons as SVG Components
const PlatformIcons: { [key: string]: React.FC<{ size?: number; color?: string }> } = {
    INSTAGRAM: ({ size = 20, color = 'white' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.069-4.849.069-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    ),
    FACEBOOK: ({ size = 20, color = 'white' }) => (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
        </svg>
    ),
    TIKTOK: ({ size = 20, color = 'white' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" fill="#000000" />
            <path d="M8.5 8.5C8.5 7.67 9.17 7 10 7s1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5S8.5 16.33 8.5 15.5v-7z" fill="#FF0050" />
            <path d="M15.5 8.5C15.5 7.67 16.17 7 17 7s1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5v-7z" fill="#00F2EA" />
        </svg>
    ),
    TWITTER: ({ size = 20, color = 'white' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    )
};

// Platform Configuration - Instagram, TikTok, and Twitter
const platformConfig: { [key: string]: { name: string; gradient: string; bgColor: string; fields: string[] } } = {
    INSTAGRAM: {
        name: 'Instagram',
        gradient: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #F77737 100%)',
        bgColor: '#E1306C',
        fields: ['accountId', 'apiKey', 'userId', 'accessToken', 'apiVersion']
    },
    FACEBOOK: {
        name: 'Facebook',
        gradient: 'linear-gradient(135deg, #1877F2 0%, #0C5CBF 100%)',
        bgColor: '#1877F2',
        fields: ['apiKey']
    },
    TIKTOK: {
        name: 'TikTok',
        gradient: 'linear-gradient(135deg, #000000 0%, #25F4EE 50%, #FE2C55 100%)',
        bgColor: '#000000',
        fields: ['clientId', 'clientSecret', 'accessToken', 'refreshToken', 'openId']
    },
    TWITTER: {
        name: 'X',
        gradient: 'linear-gradient(135deg, #333333 0%, #000000 100%)',
        bgColor: '#000000',
        fields: ['accessToken', 'refreshToken', 'userId']
    }
};

// Get platform icon component
const getPlatformIcon = (platform: string, size = 22) => {
    const IconComponent = PlatformIcons[platform?.toUpperCase()] || PlatformIcons.INSTAGRAM;
    return <IconComponent size={size} />;
};

const Settings: React.FC = () => {
    const userRole = localStorage.getItem('user_role');
    // Set default tab - everyone starts with 'general'
    const [activeTab, setActiveTab] = useState<'general' | 'social' | 'brand-voice' | 'ai'>('general');
    const [showBrandVoiceManage, setShowBrandVoiceManage] = useState(false); // For "Manage Brand Voices" sub-section
    const [showInstagramForm, setShowInstagramForm] = useState(false);
    const [showFacebookForm, setShowFacebookForm] = useState(false);
    const [showTikTokForm, setShowTikTokForm] = useState(false);
    const [showTwitterForm, setShowTwitterForm] = useState(false);
    const [showPlatformForm, setShowPlatformForm] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState<string>('INSTAGRAM');
    const navigate = useNavigate();

    // User Profile State
    const [userProfile, setUserProfile] = useState<{ name: string; email: string } | null>(null);
    const [showChangeName, setShowChangeName] = useState(false);
    const [newName, setNewName] = useState('');

    // Tokens State
    const [tokens, setTokens] = useState<any[]>([]);

    useEffect(() => {
        fetchUserProfile();
        fetchTokens();
        if (userRole === 'super_admin') {
            fetchRolePermissions();
        }
    }, []);

    const fetchUserProfile = async () => {
        try {
            const data = await getMe();
            setUserProfile(data);
            setNewName(data.name);
        } catch (error) {
            console.error('Failed to fetch user profile', error);
        }
    };

    const fetchTokens = async () => {
        try {
            const data = await getTokens();
            setTokens(data);
        } catch (error) {
            console.error('Failed to fetch tokens', error);
        }
    };

    const fetchRolePermissions = async () => {
        try {
            const response = await fetch(`${API_URL}/api/v1/auth/role-permissions`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setRolePermissions(data);
            }
        } catch (error) {
            console.error('Failed to fetch role permissions', error);
        }
    };

    const saveRolePermissions = async () => {
        setPermissionsSaveStatus({ type: null, message: '' });
        try {
            const response = await fetch(`${API_URL}/api/v1/auth/role-permissions`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(rolePermissions)
            });
            if (response.ok) {
                setPermissionsSaveStatus({ type: 'success', message: 'Role permissions updated successfully!' });
                setTimeout(() => {
                    setShowManageAccess(false);
                    setPermissionsSaveStatus({ type: null, message: '' });
                }, 2000);
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.detail || `Failed to update role permissions (Status: ${response.status})`;
                setPermissionsSaveStatus({ type: 'error', message: errorMessage });
            }
        } catch (error: any) {
            console.error('Failed to save role permissions', error);
            const errorMessage = error.message || 'Network error. Please check your connection and try again.';
            setPermissionsSaveStatus({ type: 'error', message: `Failed to save: ${errorMessage}` });
        }
    };

    const togglePermission = (role: string, permission: string) => {
        setRolePermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [permission]: !prev[role][permission]
            }
        }));
    };

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_role');
        navigate('/login');
    };

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    const handleChangePassword = async () => {
        try {
            await changePassword(newPassword);
            alert('Password changed successfully');
            setShowChangePassword(false);
            setNewPassword('');
        } catch (error: any) {
            console.error('Failed to change password', error);
            alert(error.response?.data?.detail || 'Failed to change password');
        }
    };

    const handleChangeName = async () => {
        try {
            await updateMe({ name: newName });
            setUserProfile(prev => prev ? { ...prev, name: newName } : null);
            setShowChangeName(false);
            alert('Name updated successfully');
        } catch (error: any) {
            console.error('Failed to update name', error);
            alert(error.response?.data?.detail || 'Failed to update name');
        }
    };

    // Form State
    const [accountId, setAccountId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [socialUserId, setSocialUserId] = useState('');
    const [apiVersion, setApiVersion] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [refreshToken, setRefreshToken] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    // Facebook specific
    const [pageAccessToken, setPageAccessToken] = useState('');
    const [userAccessToken, setUserAccessToken] = useState('');
    // New fields
    const [scope, setScope] = useState('');
    // Twitter-specific fields
    const [twitterApiKey, setTwitterApiKey] = useState('');
    const [twitterApiSecret, setTwitterApiSecret] = useState('');
    const [twitterAccessToken, setTwitterAccessToken] = useState('');
    const [twitterAccessTokenSecret, setTwitterAccessTokenSecret] = useState('');
    const [twitterUserId, setTwitterUserId] = useState('');
    const [twitterUsername, setTwitterUsername] = useState('');
    // OAuth 2.0 fields (optional)
    const [twitterBearerToken, setTwitterBearerToken] = useState('');
    const [twitterClientId, setTwitterClientId] = useState('');
    const [twitterClientSecret, setTwitterClientSecret] = useState('');
    const [adGroupId, setAdGroupId] = useState('');
    const [adAccountId, setAdAccountId] = useState('');
    const [adId, setAdId] = useState('');
    const [identityType, setIdentityType] = useState('');

    const [status, setStatus] = useState('');

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tokenToDelete, setTokenToDelete] = useState<number | null>(null);
    const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');

    const [showAdAccount, setShowAdAccount] = useState(false);
    
    // Add Token ID for editing
    const [tokenId, setTokenId] = useState<number | null>(null);

    // Role Permissions State
    const [showManageAccess, setShowManageAccess] = useState(false);
    const [permissionsSaveStatus, setPermissionsSaveStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
    const [rolePermissions, setRolePermissions] = useState<{ [key: string]: { [key: string]: boolean } }>({
        super_admin: {
            dashboard: true,
            comment_list: true,
            ai_response: true,
            user_comments: true,
            all_posts: true,
            statistics: true,
            settings: true,
            users_management: true
        },
        admin: {
            dashboard: true,
            comment_list: true,
            ai_response: true,
            user_comments: true,
            all_posts: true,
            statistics: true,
            settings: false,
            users_management: true
        },
        user: {
            dashboard: true,
            comment_list: true,
            ai_response: false,
            user_comments: true,
            all_posts: false,
            statistics: true,
            settings: false,
            users_management: false
        }
    });

    const resetForm = () => {
        setTokenId(null);
        setAccountId('');
        setApiKey('');
        setSocialUserId('');
        setApiVersion('');
        setAccessToken('');
        setRefreshToken('');
        setClientSecret('');
        setPageAccessToken('');
        setUserAccessToken('');
        setScope('');
        setAdGroupId('');
        setAdAccountId('');
        setAdId('');
        setIdentityType('');
        setShowAdAccount(false);
        // Twitter fields
        setTwitterApiKey('');
        setTwitterApiSecret('');
        setTwitterAccessToken('');
        setTwitterAccessTokenSecret('');
        setTwitterUserId('');
        setTwitterUsername('');
        setTwitterBearerToken('');
        setTwitterClientId('');
        setTwitterClientSecret('');
    };

    const handleDeleteToken = (id: number) => {
        setTokenToDelete(id);
        setDeleteConfirmationInput('');
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!tokenToDelete) return;

        if (deleteConfirmationInput.toLowerCase() !== 'delete') {
            return;
        }

        try {
            await deleteToken(tokenToDelete);
            fetchTokens(); // Refresh list
            setShowDeleteModal(false);
            setTokenToDelete(null);
        } catch (error) {
            console.error('Failed to delete token', error);
            alert('Failed to delete token');
        }
    };

    const handleEditToken = (token: any) => {
        // Populate form fields
        setTokenId(token.id);
        setAccountId(token.accountId || token.clientId || '');
        setApiKey(token.apiKey || '');
        setSocialUserId(token.userId || token.openId || '');
        setAccessToken(token.accessToken || '');
        setRefreshToken(token.refreshToken || '');
        setClientSecret(token.clientSecret || '');
        setPageAccessToken(token.pageAccessToken || '');
        setUserAccessToken(token.userAccessToken || '');
        setScope(token.scope || '');
        setAdGroupId(token.adGroupId || '');
        setAdAccountId(token.adAccountId || '');
        setAdId(token.adId || '');
        setIdentityType(token.identityType || '');
        setApiVersion(token.version || '');

        setShowAdAccount(!!(token.adGroupId || token.adAccountId || token.adId || token.identityType));

        if (token.platform?.toUpperCase() === 'INSTAGRAM') {
            setShowInstagramForm(true);
        } else if (token.platform?.toUpperCase() === 'TIKTOK') {
            setShowTikTokForm(true);
        } else if (token.platform?.toUpperCase() === 'FACEBOOK') {
            setShowFacebookForm(true);
        } else if (token.platform?.toUpperCase() === 'TWITTER' || token.platform?.toUpperCase() === 'X') {
            setTwitterApiKey(token.apiKey || '');
            setTwitterApiSecret(token.apiSecret || '');
            setTwitterAccessToken(token.accessToken || '');
            setTwitterAccessTokenSecret(token.accessTokenSecret || '');
            setTwitterUserId(token.userId || '');
            setTwitterUsername(token.displayUsername || token.accountId || '');
            setTwitterBearerToken(token.refreshToken || '');
            setTwitterClientId(token.clientId || '');
            setTwitterClientSecret(token.clientSecret || '');
            setShowTwitterForm(true);
        }
    };

    const handleSubmit = async (platform: 'INSTAGRAM' | 'TIKTOK' | 'TWITTER' | 'FACEBOOK') => {
        try {
            setStatus('Saving...');
            const payload: any = {
                platform: platform,
                isActive: true
            };
            if (tokenId) {
                payload.tokenId = tokenId;
            }

            if (platform === 'FACEBOOK') {
                payload.accountId = accountId;
                payload.apiKey = apiKey;
                payload.userId = socialUserId;
                payload.pageAccessToken = pageAccessToken;
                payload.userAccessToken = userAccessToken;
                payload.accessToken = pageAccessToken; // Backwards compatibility if needed
                payload.version = apiVersion;

                if (showAdAccount) {
                    payload.adGroupId = adGroupId;
                    payload.adAccountId = adAccountId;
                    payload.adId = adId;
                    payload.identityType = identityType;
                }
            } else if (platform === 'INSTAGRAM') {
                payload.accountId = accountId;
                payload.apiKey = apiKey;
                payload.userId = socialUserId;
                payload.pageAccessToken = pageAccessToken;
                payload.userAccessToken = userAccessToken;
                payload.accessToken = pageAccessToken; // Backwards compatibility
                payload.version = apiVersion;

                if (showAdAccount) {
                    payload.adGroupId = adGroupId;
                    payload.adAccountId = adAccountId;
                    payload.adId = adId;
                    payload.identityType = identityType;
                }
            } else if (platform === 'TIKTOK') {
                payload.clientId = accountId; // Client ID
                payload.apiKey = apiKey; // Marketing Access Token
                payload.clientSecret = clientSecret; // Client Secret
                payload.openId = socialUserId; // Open ID
                payload.accessToken = accessToken; // Access Token
                payload.refreshToken = refreshToken; // Refresh Token
                payload.scope = scope;
                payload.userId = newName; // User ID / Display Username
                payload.displayUsername = newName; // Display Username for SocialMediaUsers

                if (showAdAccount) {
                    payload.adGroupId = adGroupId;
                    payload.adAccountId = adAccountId;
                    payload.adId = adId;
                    payload.identityType = identityType;
                }
            } else if (platform === 'TWITTER') {
                payload.apiKey = twitterApiKey; // Twitter API Key (Consumer Key)
                payload.apiSecret = twitterApiSecret; // Twitter API Secret (Consumer Secret)
                payload.accessToken = twitterAccessToken; // Access Token
                payload.accessTokenSecret = twitterAccessTokenSecret; // Access Token Secret
                payload.userId = twitterUserId; // Twitter User ID (Numeric ID)
                payload.accountId = twitterUsername; // Twitter Username
                payload.displayUsername = twitterUsername; // Display Username for SocialMediaUsers
                payload.refreshToken = twitterBearerToken; // Store Refresh Token appropriately

                // Optional OAuth 2.0 credentials
                if (twitterClientId) {
                    payload.clientId = twitterClientId;
                }
                if (twitterClientSecret) {
                    payload.clientSecret = twitterClientSecret;
                }
            }

            await saveToken(payload);
            setStatus('Saved successfully!');
            fetchTokens(); // Refresh list
            setTimeout(() => {
                setShowInstagramForm(false);
                setShowFacebookForm(false);
                setShowTikTokForm(false);
                setShowTwitterForm(false);
                setStatus('');
                resetForm();
            }, 1500);
        } catch (error) {
            console.error("Error saving token:", error);
            setStatus('Error saving token.');
        }
    };

    const handleTwitterConnect = async () => {
        try {
            // Get Twitter OAuth authorization URL from backend
            const response = await fetch(`${API_URL}/twitter/auth-url`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to get Twitter authorization URL');
            }

            const data = await response.json();

            // Redirect to Twitter authorization page
            window.location.href = data.auth_url;
        } catch (error) {
            console.error('Error connecting Twitter:', error);
            setStatus('Error connecting to Twitter. Please try again.');
        }
    };

    // Tab configuration with unique icons and colors - Available to all users
    const tabs = [
        { id: 'general', label: 'General', icon: Shield, color: colors.primary, lightColor: colors.blueLight },
        { id: 'brand-voice', label: 'Brand Voice', icon: Palette, color: '#d97706', lightColor: '#fef3c7' },
        { id: 'social', label: 'Social Accounts', icon: Network, color: '#059669', lightColor: '#ecfdf5' },
    ] as const;

    return (
        <div className="page-container" style={{
            display: 'flex',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto',
            minHeight: 'calc(100vh - 120px)'
        }}>
            {/* Sidebar - Always Visible */}
            <div style={{
                width: '240px',
                flexShrink: 0,
                paddingTop: '0.5rem'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1.5rem',
                    paddingLeft: '0.5rem'
                }}>
                    <div style={{
                        padding: '0.5rem',
                        backgroundColor: colors.purpleLight,
                        borderRadius: '10px'
                    }}>
                        <SettingsIcon size={20} color={colors.purple} />
                    </div>
                    <h2 style={{
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        color: colors.text,
                        margin: 0,
                        fontFamily: 'Outfit, sans-serif',
                        letterSpacing: '-0.02em'
                    }}>Settings</h2>
                </div>

                <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {tabs.map(tab => {
                        const IconComponent = tab.icon;
                        const isActive = activeTab === tab.id;
                        const isBrandVoice = tab.id === 'brand-voice';

                        return (
                            <div key={tab.id}>
                                <button
                                    onClick={() => {
                                        if (tab.id === 'brand-voice') {
                                            // Toggle Brand Voice dropdown
                                            if (!isActive) {
                                                // If tab is not active, activate it and open dropdown
                                                setActiveTab(tab.id as any);
                                                setShowBrandVoiceManage(false); // Start with generation view
                                            } else {
                                                // If already active, toggle the dropdown
                                                setShowBrandVoiceManage(!showBrandVoiceManage);
                                            }
                                        } else {
                                            setActiveTab(tab.id as any);
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        borderRadius: '10px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontSize: '0.9rem',
                                        fontWeight: isActive ? '600' : '500',
                                        backgroundColor: isActive && !showBrandVoiceManage ? colors.primary : (isActive ? 'rgba(66, 133, 244, 0.8)' : 'transparent'),
                                        color: isActive ? 'white' : colors.textSecondary,
                                        transition: 'all 0.15s ease',
                                        width: '100%',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <IconComponent size={18} color={isActive ? 'white' : colors.gray} />
                                        {tab.label}
                                    </div>
                                    {/* Chevron only for Brand Voice tab when active */}
                                    {isBrandVoice && isActive && (
                                        <ChevronDown
                                            size={16}
                                            color="white"
                                            style={{
                                                transform: showBrandVoiceManage ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease'
                                            }}
                                        />
                                    )}
                                </button>

                                {/* Sub-item for "Manage Brand Voices" - shows when Brand Voice is active */}
                                {isBrandVoice && isActive && (
                                    <div style={{
                                        marginTop: '0.25rem',
                                        marginLeft: '3rem' // Push the entire button container to the right
                                    }}>
                                        <button
                                            onClick={() => setShowBrandVoiceManage(true)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                padding: '0.5rem 1rem', // Simplified padding - no left padding needed since container is shifted
                                                borderRadius: '10px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                fontSize: '0.85rem',
                                                fontWeight: showBrandVoiceManage ? '600' : '500',
                                                backgroundColor: showBrandVoiceManage ? 'rgba(66, 133, 244, 0.1)' : 'transparent',
                                                color: showBrandVoiceManage ? colors.primary : colors.textSecondary,
                                                transition: 'all 0.15s ease',
                                                width: '100%'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!showBrandVoiceManage) {
                                                    e.currentTarget.style.backgroundColor = 'rgba(66, 133, 244, 0.05)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!showBrandVoiceManage) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }
                                            }}
                                        >
                                            <span>Manage Brand Voices</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </div>

            {/* Main Content */}
            <div style={{
                flex: 1,
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '2rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                border: `1px solid ${colors.border}`
            }}>
                {activeTab === 'general' && (
                    <div>
                        {/* Section Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            marginBottom: '1.5rem',
                            paddingBottom: '1rem',
                            borderBottom: `1px solid ${colors.border}`
                        }}>
                            <div style={{ padding: '0.5rem', backgroundColor: colors.blueLight, borderRadius: '8px' }}>
                                <Shield size={20} color={colors.primary} />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0, fontFamily: 'Outfit, sans-serif' }}>General Settings</h3>
                                <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0, fontFamily: 'Inter, sans-serif' }}>Manage your account information</p>
                            </div>
                        </div>

                        {/* Profile Section */}
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '1.5rem',
                            marginBottom: '1.5rem',
                            border: `1px solid ${colors.border}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: colors.text, marginBottom: '1.25rem', fontFamily: 'Outfit, sans-serif' }}>Profile Information</h4>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '1rem 0',
                                borderBottom: `1px solid ${colors.border}`
                            }}>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>Full Name</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '500', color: colors.text }}>{userProfile?.name || 'Loading...'}</div>
                                </div>
                                <button
                                    onClick={() => setShowChangeName(true)}
                                    style={{
                                        padding: '0.625rem 1rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '10px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: colors.text,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
                                >
                                    <Edit2 size={16} />
                                    Edit
                                </button>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '1rem 0'
                            }}>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>Email Address</div>
                                    <div style={{ fontSize: '1rem', fontWeight: '500', color: colors.text }}>{userProfile?.email || 'Loading...'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Security Section */}
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '1.5rem',
                            marginBottom: '1.5rem',
                            border: `1px solid ${colors.border}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: colors.text, marginBottom: '1.25rem', fontFamily: 'Outfit, sans-serif' }}>Security</h4>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '1rem 0',
                                borderBottom: `1px solid ${colors.border}`
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ padding: '0.75rem', backgroundColor: colors.grayLight, borderRadius: '10px' }}>
                                        <Key size={20} color={colors.gray} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '1rem', fontWeight: '500', color: colors.text }}>Password</div>
                                        <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>Change your account password</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowChangePassword(true)}
                                    style={{
                                        padding: '0.625rem 1rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '10px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: colors.text,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
                                >
                                    Change
                                </button>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '1rem 0'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ padding: '0.75rem', backgroundColor: colors.dangerLight, borderRadius: '10px' }}>
                                        <LogOut size={20} color={colors.danger} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '1rem', fontWeight: '500', color: colors.text }}>Log Out</div>
                                        <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>Sign out of this device</div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    style={{
                                        padding: '0.625rem 1rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${colors.danger}`,
                                        borderRadius: '10px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: colors.danger,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.dangerLight}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    Log Out
                                </button>
                            </div>
                        </div>

                        {/* Manage Access Section - Only for Super Admin */}
                        {userRole === 'super_admin' && (
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '16px',
                                padding: '1.5rem',
                                marginBottom: '1.5rem',
                                border: `1px solid ${colors.border}`,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}>
                                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: colors.text, marginBottom: '1.25rem', fontFamily: 'Outfit, sans-serif' }}>Access Control</h4>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem 0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{ padding: '0.75rem', backgroundColor: colors.blueLight, borderRadius: '10px' }}>
                                            <Users size={20} color={colors.primary} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '1rem', fontWeight: '500', color: colors.text }}>Manage Role Permissions</div>
                                            <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>Configure access levels for Admin and User roles</div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowManageAccess(true)}
                                        style={{
                                            padding: '0.625rem 1rem',
                                            backgroundColor: 'white',
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '10px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            color: colors.text,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.primary}
                                        onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
                                    >
                                        Manage
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Change Name Modal */}
                        {showChangeName && (
                            <div style={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', zIndex: 1000
                            }}>
                                <div style={{
                                    backgroundColor: 'white', borderRadius: '16px',
                                    maxWidth: '400px', width: '90%', padding: '1.5rem',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>Change Name</h3>
                                        <button onClick={() => setShowChangeName(false)} style={{ padding: '0.375rem', backgroundColor: colors.grayLight, borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                            <X size={16} color={colors.gray} />
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Full Name"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            marginBottom: '1.25rem',
                                            border: `2px solid ${colors.border}`,
                                            borderRadius: '10px',
                                            fontSize: '0.95rem',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowChangeName(false)}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleChangeName}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 6px rgba(99,102,241,0.3)'
                                            }}
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Change Password Modal */}
                        {showChangePassword && (
                            <div style={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', zIndex: 1000
                            }}>
                                <div style={{
                                    backgroundColor: 'white', borderRadius: '16px',
                                    maxWidth: '400px', width: '90%', padding: '1.5rem',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>Change Password</h3>
                                        <button onClick={() => setShowChangePassword(false)} style={{ padding: '0.375rem', backgroundColor: colors.grayLight, borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                            <X size={16} color={colors.gray} />
                                        </button>
                                    </div>
                                    <input
                                        type="password"
                                        placeholder="New Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            marginBottom: '1.25rem',
                                            border: `2px solid ${colors.border}`,
                                            borderRadius: '10px',
                                            fontSize: '0.95rem',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowChangePassword(false)}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleChangePassword}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 6px rgba(99,102,241,0.3)'
                                            }}
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Manage Access Modal */}
                        {showManageAccess && userRole === 'super_admin' && (
                            <div style={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', zIndex: 1000
                            }} onClick={() => setShowManageAccess(false)}>
                                <div style={{
                                    backgroundColor: 'white', borderRadius: '16px',
                                    maxWidth: '700px', width: '90%', padding: '1.5rem',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                                    maxHeight: '90vh', overflowY: 'auto'
                                }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', position: 'sticky', top: 0, backgroundColor: 'white', paddingBottom: '1rem', borderBottom: `1px solid ${colors.border}` }}>
                                        <div>
                                            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: colors.text, margin: 0 }}>Manage Role Permissions</h3>
                                            <p style={{ fontSize: '0.875rem', color: colors.textSecondary, margin: '0.25rem 0 0 0' }}>Configure what each role can access</p>
                                        </div>
                                        <button onClick={() => setShowManageAccess(false)} style={{ padding: '0.375rem', backgroundColor: colors.grayLight, borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
                                            <X size={16} color={colors.gray} />
                                        </button>
                                    </div>

                                    <div style={{ marginBottom: '1.5rem' }}>
                                        {/* Permission Matrix */}
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: colors.grayLight }}>
                                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.text, borderRadius: '8px 0 0 0' }}>Feature</th>
                                                        <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: colors.text }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                                <Shield size={16} color="#dc2626" />
                                                                Super Admin
                                                            </div>
                                                        </th>
                                                        <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: colors.text }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                                <Lock size={16} color={colors.primary} />
                                                                Admin
                                                            </div>
                                                        </th>
                                                        <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: colors.text, borderRadius: '0 8px 0 0' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                                                <Users size={16} color={colors.gray} />
                                                                User
                                                            </div>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[
                                                        { key: 'dashboard', label: 'Dashboard' },
                                                        { key: 'comment_list', label: 'Comment List' },
                                                        { key: 'ai_response', label: 'AI Response' },
                                                        { key: 'user_comments', label: 'User Comments Tab' },
                                                        { key: 'all_posts', label: 'All Posts Tab' },
                                                        { key: 'statistics', label: 'Statistics' },
                                                        { key: 'users_management', label: 'Users Management' },
                                                        { key: 'settings', label: 'Settings' }
                                                    ].map((feature, idx) => (
                                                        <tr key={feature.key} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                                            <td style={{ padding: '0.875rem', fontSize: '0.875rem', color: colors.text }}>{feature.label}</td>
                                                            <td style={{ padding: '0.875rem', textAlign: 'center' }}>
                                                                <div style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    backgroundColor: colors.successLight,
                                                                    borderRadius: '8px',
                                                                    cursor: 'not-allowed'
                                                                }}>
                                                                    <input type="checkbox" checked={true} disabled style={{ width: '18px', height: '18px', cursor: 'not-allowed' }} />
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '0.875rem', textAlign: 'center' }}>
                                                                <div style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    backgroundColor: rolePermissions.admin?.[feature.key] ? colors.successLight : colors.grayLight,
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        togglePermission('admin', feature.key);
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={rolePermissions.admin?.[feature.key] || false}
                                                                        readOnly
                                                                        style={{ width: '18px', height: '18px', cursor: 'pointer', pointerEvents: 'none' }}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '0.875rem', textAlign: 'center' }}>
                                                                <div style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    backgroundColor: rolePermissions.user?.[feature.key] ? colors.successLight : colors.grayLight,
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        togglePermission('user', feature.key);
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={rolePermissions.user?.[feature.key] || false}
                                                                        readOnly
                                                                        style={{ width: '18px', height: '18px', cursor: 'pointer', pointerEvents: 'none' }}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div style={{
                                            marginTop: '1.5rem',
                                            padding: '1rem',
                                            backgroundColor: colors.blueLight,
                                            borderRadius: '10px',
                                            borderLeft: `4px solid ${colors.primary}`
                                        }}>
                                            <p style={{ fontSize: '0.875rem', color: colors.text, margin: 0, lineHeight: '1.5' }}>
                                                <strong>Note:</strong> Super Admin always has full access to all features. Changes affect Admin and User roles only.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Status Message */}
                                    {permissionsSaveStatus.type && (
                                        <div style={{
                                            padding: '0.75rem 1rem',
                                            marginBottom: '1rem',
                                            backgroundColor: permissionsSaveStatus.type === 'success' ? colors.successLight : colors.dangerLight,
                                            border: `1px solid ${permissionsSaveStatus.type === 'success' ? colors.success : colors.danger}`,
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            color: permissionsSaveStatus.type === 'success' ? colors.success : colors.danger,
                                            fontWeight: '500'
                                        }}>
                                            {permissionsSaveStatus.message}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', position: 'sticky', bottom: 0, backgroundColor: 'white', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
                                        <button
                                            onClick={() => {
                                                setShowManageAccess(false);
                                                setPermissionsSaveStatus({ type: null, message: '' });
                                            }}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={saveRolePermissions}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 6px rgba(66,133,244,0.3)'
                                            }}
                                        >
                                            Save Permissions
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'brand-voice' && (
                    <div>
                        {!showBrandVoiceManage ? (
                            /* Main Brand Voice Page */
                            <div>
                                {/* Section Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{ padding: '0.5rem', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                                        <Palette size={20} color="#d97706" />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Brand Voice</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0, fontFamily: 'Inter, sans-serif' }}>Define your brand's tone and communication style</p>
                                    </div>
                                </div>
                                <BrandVoiceSettings
                                    key="generation-view"
                                    hideHeader={true}
                                    initialStep="upload"
                                />
                            </div>
                        ) : (
                            /* Manage Brand Voices Page */
                            <div>
                                {/* Section Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{ padding: '0.5rem', backgroundColor: '#d1fae5', borderRadius: '8px' }}>
                                        <RefreshCw size={20} color="#059669" />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Manage Brand Voices</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0, fontFamily: 'Inter, sans-serif' }}>View, edit, and manage your saved brand voices</p>
                                    </div>
                                </div>

                                {/* Use BrandVoiceSettings in history mode */}
                                <BrandVoiceSettings
                                    key="manage-view"
                                    hideHeader={true}
                                    initialStep="history"
                                    onRegeneratePlatform={(platform) => {
                                        // When regenerating, switch back to generation view
                                        setShowBrandVoiceManage(false);
                                    }}
                                    onBackToGeneration={() => {
                                        // When clicking back button, switch to generation view
                                        setShowBrandVoiceManage(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'social' && (
                    <div>
                        {!showFacebookForm && !showInstagramForm && !showTikTokForm && !showTwitterForm && !showPlatformForm && (
                            <>
                                {/* Section Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{ padding: '0.5rem', backgroundColor: '#ecfdf5', borderRadius: '8px' }}>
                                        <Network size={20} color="#059669" />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Social Accounts</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0, fontFamily: 'Inter, sans-serif' }}>Connect your social media platforms</p>
                                    </div>
                                </div>

                                {/* Platform Grid */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                    gap: '1rem',
                                    marginBottom: '2rem'
                                }}>
                                    {Object.entries(
                                        userRole === 'super_admin'
                                            ? platformConfig
                                            : { FACEBOOK: platformConfig.FACEBOOK, INSTAGRAM: platformConfig.INSTAGRAM, TIKTOK: platformConfig.TIKTOK, TWITTER: platformConfig.TWITTER }
                                    ).map(([key, config]) => (
                                        <div
                                            key={key}
                                            onClick={() => {
                                                resetForm();
                                                setSelectedPlatform(key as any);
                                                setShowAdAccount(false);
                                                if (key === 'TIKTOK') {
                                                    setShowTikTokForm(true);
                                                } else if (key === 'FACEBOOK') {
                                                    setShowFacebookForm(true);
                                                } else if (key === 'INSTAGRAM') {
                                                    setShowInstagramForm(true);
                                                } else if (key === 'TWITTER') {
                                                    setShowTwitterForm(true);
                                                } else {
                                                    setShowPlatformForm(true);
                                                }
                                            }}
                                            style={{
                                                padding: '1.5rem 1.25rem',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                borderRadius: '16px',
                                                border: `2px solid ${colors.border}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '1rem',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                minHeight: '120px',
                                                justifyContent: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                                                e.currentTarget.style.boxShadow = `0 12px 28px ${config.bgColor || colors.primary}20`;
                                                e.currentTarget.style.borderColor = config.bgColor || colors.primary;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                                                e.currentTarget.style.borderColor = colors.border;
                                            }}
                                        >
                                            {/* Background Glow Effect */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '-50%',
                                                left: '-50%',
                                                width: '200%',
                                                height: '200%',
                                                background: `radial-gradient(circle, ${config.bgColor || colors.primary}08 0%, transparent 70%)`,
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                                pointerEvents: 'none'
                                            }} />

                                            <div style={{
                                                width: '48px',
                                                height: '48px',
                                                borderRadius: '12px',
                                                background: config.gradient,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                boxShadow: `0 8px 20px ${config.bgColor || colors.primary}30`,
                                                border: '2px solid white',
                                                position: 'relative',
                                                zIndex: 1
                                            }}>
                                                {getPlatformIcon(key, 22)}
                                            </div>

                                            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                                                <div style={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    color: colors.text,
                                                    fontFamily: 'Outfit, sans-serif',
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    {config.name}
                                                </div>
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    color: colors.textSecondary,
                                                    fontFamily: 'Inter, sans-serif',
                                                    opacity: 0.8
                                                }}>
                                                    Click to connect
                                                </div>
                                            </div>

                                            {/* Hover indicator */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '1rem',
                                                right: '1rem',
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: config.bgColor || colors.primary,
                                                opacity: 0,
                                                transform: 'scale(0)',
                                                transition: 'all 0.2s ease'
                                            }} />
                                        </div>
                                    ))}
                                </div>

                                {/* Connected Accounts List */}
                                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: colors.text, marginBottom: '1rem', marginTop: '2rem', fontFamily: 'Outfit, sans-serif' }}>Connected Accounts ({tokens.length})</h4>

                                <div className="accounts-list" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                    gap: '1rem'
                                }}>
                                    {tokens.map((token) => (
                                        <div key={token.id} className="account-item" style={{
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '12px',
                                            padding: '1rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                            transition: 'all 0.3s ease',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            minHeight: '180px'
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
                                                e.currentTarget.style.borderColor = colors.primary;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                                                e.currentTarget.style.borderColor = colors.border;
                                            }}>

                                            <div className="account-info" style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                flex: 1,
                                                textAlign: 'center'
                                            }}>
                                                <div style={{
                                                    position: 'relative',
                                                    zIndex: 1
                                                }}>
                                                    <div style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        borderRadius: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: platformConfig[token.platform]?.gradient || colors.primary,
                                                        boxShadow: `0 6px 12px ${platformConfig[token.platform]?.bgColor || colors.primary}25`,
                                                        border: '2px solid white'
                                                    }}>
                                                        {getPlatformIcon(token.platform, 20)}
                                                    </div>
                                                    {/* Status indicator */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: '-3px',
                                                        right: '-3px',
                                                        width: '16px',
                                                        height: '16px',
                                                        backgroundColor: colors.success,
                                                        border: '2px solid white',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <div style={{
                                                            width: '4px',
                                                            height: '4px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%'
                                                        }} />
                                                    </div>
                                                </div>

                                                <div style={{ flex: 1, width: '100%' }}>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        fontSize: '0.95rem',
                                                        color: colors.text,
                                                        fontFamily: 'Outfit, sans-serif',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {platformConfig[token.platform]?.name || token.platform}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: colors.textSecondary,
                                                        fontFamily: 'Inter, sans-serif',
                                                        fontWeight: '500',
                                                        marginBottom: '0.5rem',
                                                        wordBreak: 'break-all'
                                                    }}>
                                                        {(token.accountId || token.userId || 'Connected').length > 20
                                                            ? `${(token.accountId || token.userId || 'Connected').substring(0, 20)}...`
                                                            : (token.accountId || token.userId || 'Connected')
                                                        }
                                                    </div>
                                                    <div style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: token.platform === 'TIKTOK' && token.accessToken ? colors.successLight : token.platform === 'TIKTOK' ? colors.warningLight : colors.successLight,
                                                        color: token.platform === 'TIKTOK' && token.accessToken ? colors.success : token.platform === 'TIKTOK' ? colors.warning : colors.success,
                                                        borderRadius: '12px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em'
                                                    }}>
                                                        <div style={{
                                                            width: '4px',
                                                            height: '4px',
                                                            backgroundColor: token.platform === 'TIKTOK' && token.accessToken ? colors.success : token.platform === 'TIKTOK' ? colors.warning : colors.success,
                                                            borderRadius: '50%'
                                                        }} />
                                                        {token.platform === 'TIKTOK' && token.accessToken ? 'Authorized' : token.platform === 'TIKTOK' ? 'Connected' : 'Active'}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                gap: '0.5rem',
                                                zIndex: 1,
                                                width: '100%',
                                                marginTop: '0.75rem'
                                            }}>
                                                <button
                                                    onClick={() => handleEditToken(token)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '0.5rem 0.75rem',
                                                        backgroundColor: 'white',
                                                        border: `1px solid ${colors.border}`,
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '500',
                                                        color: colors.text,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.375rem',
                                                        fontFamily: 'Inter, sans-serif',
                                                        transition: 'all 0.2s ease',
                                                        justifyContent: 'center'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.borderColor = colors.primary;
                                                        e.currentTarget.style.backgroundColor = colors.blueLight;
                                                        e.currentTarget.style.color = colors.primary;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.borderColor = colors.border;
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                        e.currentTarget.style.color = colors.text;
                                                    }}
                                                >
                                                    <Edit2 size={14} />
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteToken(token.id)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '0.5rem 0.75rem',
                                                        backgroundColor: colors.dangerLight,
                                                        border: `1px solid ${colors.dangerLight}`,
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '500',
                                                        color: colors.danger,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.375rem',
                                                        fontFamily: 'Inter, sans-serif',
                                                        transition: 'all 0.2s ease',
                                                        justifyContent: 'center'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = colors.danger;
                                                        e.currentTarget.style.borderColor = colors.danger;
                                                        e.currentTarget.style.color = 'white';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = colors.dangerLight;
                                                        e.currentTarget.style.borderColor = colors.dangerLight;
                                                        e.currentTarget.style.color = colors.danger;
                                                    }}
                                                >
                                                    <Trash2 size={14} />
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Generic Platform Form */}
                        {showPlatformForm && (
                            <div>
                                <button
                                    onClick={() => setShowPlatformForm(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: colors.grayLight,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Back to Platforms
                                </button>

                                {/* Platform Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        background: platformConfig[selectedPlatform]?.gradient || platformConfig.INSTAGRAM.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}>
                                        {getPlatformIcon(selectedPlatform, 24)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>
                                            {platformConfig[selectedPlatform]?.name || 'Platform'} Connection
                                        </h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>
                                            Enter your API credentials to connect
                                        </p>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '600px' }}>
                                    <div style={{
                                        backgroundColor: colors.grayLight,
                                        padding: '1rem',
                                        borderRadius: '10px',
                                        marginBottom: '1.5rem',
                                        border: `1px solid ${colors.border}`
                                    }}>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>
                                            Connect your {platformConfig[selectedPlatform]?.name} account to enable post synchronization and AI-powered comment replies.
                                        </p>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.text }}>
                                            {selectedPlatform === 'X' ? 'API Key' : 'Account ID / Client ID'}
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter your account identifier"
                                            value={accountId}
                                            onChange={(e) => setAccountId(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 1rem',
                                                border: `2px solid ${colors.border}`,
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.text }}>
                                            API Key / Secret
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter your API key or secret"
                                            value={apiKey}
                                            onChange={(e) => setApiKey(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 1rem',
                                                border: `2px solid ${colors.border}`,
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.text }}>
                                            Access Token
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter your access token"
                                            value={accessToken}
                                            onChange={(e) => setAccessToken(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem 1rem',
                                                border: `2px solid ${colors.border}`,
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>

                                    {(selectedPlatform === 'TIKTOK' || selectedPlatform === 'YOUTUBE' || selectedPlatform === 'LINKEDIN' || selectedPlatform === 'REDDIT' || selectedPlatform === 'SNAPCHAT') && (
                                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.text }}>
                                                Refresh Token
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter your refresh token"
                                                value={refreshToken}
                                                onChange={(e) => setRefreshToken(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `2px solid ${colors.border}`,
                                                    borderRadius: '10px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>
                                    )}

                                    {(selectedPlatform === 'INSTAGRAM' || selectedPlatform === 'FACEBOOK' || selectedPlatform === 'THREADS') && (
                                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: colors.text }}>
                                                API Version
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., v19.0"
                                                value={apiVersion}
                                                onChange={(e) => setApiVersion(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `2px solid ${colors.border}`,
                                                    borderRadius: '10px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                                        {status && (
                                            <span style={{
                                                alignSelf: 'center',
                                                marginRight: 'auto',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '500',
                                                backgroundColor: status.includes('Error') ? colors.dangerLight : colors.successLight,
                                                color: status.includes('Error') ? colors.danger : colors.success
                                            }}>
                                                {status}
                                            </span>
                                        )}
                                        <button
                                            onClick={() => setShowPlatformForm(false)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => handleSubmit(selectedPlatform as any)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(99,102,241,0.3)'
                                            }}
                                        >
                                            Connect {platformConfig[selectedPlatform]?.name}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showInstagramForm && (
                            <div>
                                <button
                                    onClick={() => setShowInstagramForm(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: colors.grayLight,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Back to Accounts
                                </button>

                                {/* Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #F77737 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getPlatformIcon('INSTAGRAM', 22)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>Instagram Account</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>Connect your Instagram account credentials</p>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '600px' }}>
                                    {/* Step 1: OAuth Authorization */}
                                    <div style={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '1.5rem',
                                        backgroundColor: colors.grayLight
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Step 1: Authorize with Instagram</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            Click the button below to authorize this application with your Instagram account.
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(`${API_URL}/api/v1/auth/meta/auth-url`);
                                                    const data = await response.json();

                                                    if (!data.auth_url) {
                                                        setStatus('Failed to get authorization URL');
                                                        return;
                                                    }

                                                    const width = 600;
                                                    const height = 700;
                                                    const left = window.screenX + (window.outerWidth - width) / 2;
                                                    const top = window.screenY + (window.outerHeight - height) / 2;
                                                    const popup = window.open(
                                                        data.auth_url,
                                                        'Instagram Authorization',
                                                        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
                                                    );

                                                    if (!popup) {
                                                        setStatus('Popup blocked. Please allow popups and try again.');
                                                        return;
                                                    }

                                                    const handleMessage = (event: MessageEvent) => {
                                                        if (event.data?.type === 'INSTAGRAM_AUTH_SUCCESS') {
                                                            const authData = event.data.data;
                                                            if (authData.accessToken) setAccessToken(authData.accessToken);
                                                            if (authData.pageAccessToken) setPageAccessToken(authData.pageAccessToken);
                                                            if (authData.userAccessToken) setUserAccessToken(authData.userAccessToken);
                                                            if (authData.userId) setSocialUserId(authData.userId);
                                                            if (authData.accountId) setAccountId(authData.accountId);

                                                            setStatus('Instagram authorized successfully! Fields auto-populated.');
                                                            fetchTokens();
                                                            window.removeEventListener('message', handleMessage);
                                                        } else if (event.data?.type === 'INSTAGRAM_AUTH_ERROR') {
                                                            setStatus(`Authorization failed: ${event.data.description || event.data.error}`);
                                                            window.removeEventListener('message', handleMessage);
                                                        }
                                                    };
                                                    window.addEventListener('message', handleMessage);
                                                } catch (error) {
                                                    setStatus('Failed to initiate Instagram authorization');
                                                    console.error('Instagram auth error:', error);
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem 1.5rem',
                                                background: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #F77737 100%)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg> 
                                            Authorize with Instagram
                                        </button>
                                    </div>

                                    {!showAdAccount && (
                                        <>
                                            <h4>Standard Account Information</h4>
                                            <p className="form-description">This gives us access to your Instagram account's post and comments</p>

                                            <div className="form-group">
                                                <label>Account ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Account"
                                                    value={accountId}
                                                    onChange={(e) => setAccountId(e.target.value)}
                                                />
                                                <span className="helper-text">Always numeric, usually 10-16 digits</span>
                                            </div>

                                            <div className="form-group">
                                                <label>Instagram API Key</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Instagram API Key"
                                                    value={apiKey}
                                                    onChange={(e) => setApiKey(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Social Media User ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Social Media User ID"
                                                    value={socialUserId}
                                                    onChange={(e) => setSocialUserId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Meta API Version</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Meta API Version"
                                                    value={apiVersion}
                                                    onChange={(e) => setApiVersion(e.target.value)}
                                                />
                                                <span className="helper-text">For example: v19.0, v20.0, etc.</span>
                                            </div>

                                            <div className="form-group">
                                                <label>Page Access Token</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Page Access Token"
                                                    value={pageAccessToken}
                                                    onChange={(e) => setPageAccessToken(e.target.value)}
                                                />
                                                <span className="helper-text">Used for Page-level actions</span>
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>User Access Token</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter User Access Token"
                                                    value={userAccessToken}
                                                    onChange={(e) => setUserAccessToken(e.target.value)}
                                                />
                                                <span className="helper-text">Used for User-level actions</span>
                                            </div>
                                        </>
                                    )}

                                    {!showAdAccount ? (
                                        null
                                    ) : (
                                        <>
                                            <h3>Advertisement Account Information</h3>
                                            <p className="form-description">This gives access to your Instagram advertisement account's post and comments</p>
                                            {/* Add Instagram Ad Fields here if needed, for now just the remove button */}
                                            <div className="form-group">
                                                <label>Advertisement Group ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="1234567890123456"
                                                    value={adGroupId}
                                                    onChange={(e) => setAdGroupId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Advertisement Account ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="9876543210987654"
                                                    value={adAccountId}
                                                    onChange={(e) => setAdAccountId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>AD ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="1122334455667788"
                                                    value={adId}
                                                    onChange={(e) => setAdId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Identity Type</label>
                                                <input
                                                    type="text"
                                                    placeholder="Advertiser"
                                                    value={identityType}
                                                    onChange={(e) => setIdentityType(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="form-actions">
                                        {status && <span style={{ marginRight: '1rem', alignSelf: 'center', color: status.includes('Error') ? 'red' : 'green' }}>{status}</span>}
                                        <button className="btn-outline" onClick={() => setShowInstagramForm(false)}>Cancel</button>
                                        <button className="btn-primary" onClick={() => handleSubmit('INSTAGRAM')}>Submit</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showTikTokForm && (
                            <div>
                                <button
                                    onClick={() => setShowTikTokForm(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: colors.grayLight,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Back to Accounts
                                </button>

                                {/* Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #000000 0%, #25F4EE 50%, #FE2C55 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getPlatformIcon('TIKTOK', 22)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>TikTok Account Connection</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>Connect your TikTok account to enable sync</p>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '600px' }}>
                                    {/* Step 1: OAuth Authorization - Matching VJ's Implementation */}
                                    <div style={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '1.5rem',
                                        backgroundColor: colors.grayLight
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Step 1: Authorize with TikTok</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            Click the button below to authorize this application with your TikTok account. You'll be redirected to TikTok to grant permissions.
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    // Get the authorization URL from backend
                                                    const response = await fetch(`${API_URL}/api/v1/auth/tiktok/auth-url`);
                                                    const data = await response.json();

                                                    if (!data.auth_url) {
                                                        setStatus('Failed to get authorization URL');
                                                        return;
                                                    }

                                                    // Open TikTok OAuth in a popup window
                                                    const width = 600;
                                                    const height = 700;
                                                    const left = window.screenX + (window.outerWidth - width) / 2;
                                                    const top = window.screenY + (window.outerHeight - height) / 2;
                                                    const popup = window.open(
                                                        data.auth_url,
                                                        'TikTok Authorization',
                                                        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
                                                    );

                                                    // Check if popup was blocked
                                                    if (!popup) {
                                                        setStatus('Popup blocked. Please allow popups and try again.');
                                                        return;
                                                    }

                                                    // Listen for message from popup
                                                    const handleMessage = (event: MessageEvent) => {
                                                        if (event.data?.type === 'TIKTOK_AUTH_SUCCESS') {
                                                            const data = event.data.data;
                                                            // Auto-populate form fields from OAuth response
                                                            setAccountId(data.clientId || '');
                                                            setSocialUserId(data.openId || '');
                                                            setAccessToken(data.accessToken || '');
                                                            setRefreshToken(data.refreshToken || '');
                                                            setScope(data.scope || '');
                                                            setClientSecret(data.clientSecret || '');
                                                            setNewName(data.displayName || data.username || '');
                                                            setStatus('TikTok authorized successfully! Fields auto-populated.');
                                                            fetchTokens(); // Refresh tokens list
                                                            window.removeEventListener('message', handleMessage);
                                                        } else if (event.data?.type === 'TIKTOK_AUTH_ERROR') {
                                                            setStatus(`Authorization failed: ${event.data.description || event.data.error}`);
                                                            window.removeEventListener('message', handleMessage);
                                                        }
                                                    };
                                                    window.addEventListener('message', handleMessage);
                                                } catch (error) {
                                                    setStatus('Failed to initiate TikTok authorization');
                                                    console.error('TikTok auth error:', error);
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem 1.5rem',
                                                backgroundColor: '#4285F4',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3367D6'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4285F4'}
                                        >
                                            🔐 Authorize with TikTok
                                        </button>
                                    </div>

                                    {/* Standard Account Information */}
                                    <div style={{
                                        marginBottom: '1.5rem'
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Standard Account Information</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            This gives us access to your TikTok account's posts and comments
                                        </p>

                                        {!showAdAccount && (
                                            <>
                                                <div className="form-group">
                                                    <label>Display Username</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Display Username"
                                                        value={newName}
                                                        onChange={(e) => setNewName(e.target.value)}
                                                    />
                                                    <span className="helper-text">This username will be displayed on the platform.</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Client ID</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Client ID"
                                                        value={accountId}
                                                        onChange={(e) => setAccountId(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter your Client ID from the TikTok Developer Portal.</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Marketing Access Token</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Marketing Access Token"
                                                        value={apiKey}
                                                        onChange={(e) => setApiKey(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter your Marketing API access token (from Business Center).</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Client Secret</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Client Secret"
                                                        value={clientSecret}
                                                        onChange={(e) => setClientSecret(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter your TikTok App Client Secret. Keep this value private.</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Open ID</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Open ID"
                                                        value={socialUserId}
                                                        onChange={(e) => setSocialUserId(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter the TikTok Open ID received after OAuth authorization.</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Access Token</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Access Token"
                                                        value={accessToken}
                                                        onChange={(e) => setAccessToken(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter the TikTok Access Token returned after login. Used for making API calls</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Refresh Token</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter Refresh Token"
                                                        value={refreshToken}
                                                        onChange={(e) => setRefreshToken(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter the Refresh Token received during OAuth. Used to generate new access tokens.</span>
                                                </div>

                                                <div className="form-group">
                                                    <label>Scope</label>
                                                    <input
                                                        type="text"
                                                        placeholder='["user.info.basic", "video.upload", "comment.write"]'
                                                        value={scope}
                                                        onChange={(e) => setScope(e.target.value)}
                                                    />
                                                    <span className="helper-text">Enter the permission scopes returned from OAuth (e.g., 'video.upload comment.write').</span>
                                                </div>
                                            </>
                                        )}

                                        <div className="form-actions">
                                            {status && <span style={{ marginRight: '1rem', alignSelf: 'center', color: status.includes('Error') ? 'red' : 'green' }}>{status}</span>}
                                            <button className="btn-outline" onClick={() => setShowTikTokForm(false)}>Cancel</button>
                                            <button className="btn-primary" onClick={() => handleSubmit('TIKTOK')}>Submit</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showFacebookForm && (
                            <div>
                                <button
                                    onClick={() => setShowFacebookForm(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: colors.grayLight,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Back to Accounts
                                </button>

                                {/* Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #1877F2 0%, #0c5abb 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getPlatformIcon('FACEBOOK', 22)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>Facebook Account</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>Connect your Facebook account credentials</p>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '600px' }}>
                                    {/* Step 1: OAuth Authorization */}
                                    <div style={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '1.5rem',
                                        backgroundColor: colors.grayLight
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Step 1: Authorize with Facebook</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            Click the button below to authorize this application with your Facebook account.
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(`${API_URL}/api/v1/auth/facebook/auth-url`);
                                                    const data = await response.json();

                                                    if (!data.auth_url) {
                                                        setStatus('Failed to get authorization URL');
                                                        return;
                                                    }

                                                    const width = 600;
                                                    const height = 700;
                                                    const left = window.screenX + (window.outerWidth - width) / 2;
                                                    const top = window.screenY + (window.outerHeight - height) / 2;
                                                    const popup = window.open(
                                                        data.auth_url,
                                                        'Facebook Authorization',
                                                        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
                                                    );

                                                    if (!popup) {
                                                        setStatus('Popup blocked. Please allow popups and try again.');
                                                        return;
                                                    }

                                                    const handleMessage = (event: MessageEvent) => {
                                                        if (event.data?.type === 'FACEBOOK_AUTH_SUCCESS') {
                                                            const data = event.data.data;
                                                            if (data.accessToken) setAccessToken(data.accessToken);
                                                            if (data.pageAccessToken) setPageAccessToken(data.pageAccessToken);
                                                            if (data.userAccessToken) setUserAccessToken(data.userAccessToken);
                                                            if (data.userId) setSocialUserId(data.userId);
                                                            if (data.accountId) setAccountId(data.accountId);

                                                            setStatus('Facebook authorized successfully! Fields auto-populated.');
                                                            fetchTokens();
                                                            window.removeEventListener('message', handleMessage);
                                                        } else if (event.data?.type === 'FACEBOOK_AUTH_ERROR') {
                                                            setStatus(`Authorization failed: ${event.data.description || event.data.error}`);
                                                            window.removeEventListener('message', handleMessage);
                                                        }
                                                    };
                                                    window.addEventListener('message', handleMessage);
                                                } catch (error) {
                                                    setStatus('Failed to initiate Facebook authorization');
                                                    console.error('Facebook auth error:', error);
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem 1.5rem',
                                                backgroundColor: '#1877F2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0c5abb'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1877F2'}
                                        >
                                            🔐 Authorize with Facebook
                                        </button>
                                    </div>

                                    {!showAdAccount && (
                                        <>
                                            <h4>Standard Account Information</h4>
                                            <p className="form-description">This gives us access to your Facebook account's post and comments</p>

                                            <div className="form-group">
                                                <label>Account ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Account"
                                                    value={accountId}
                                                    onChange={(e) => setAccountId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Facebook API Key</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Facebook API Key"
                                                    value={apiKey}
                                                    onChange={(e) => setApiKey(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Social Media User ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Social Media User ID"
                                                    value={socialUserId}
                                                    onChange={(e) => setSocialUserId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Meta API Version</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Meta API Version"
                                                    value={apiVersion}
                                                    onChange={(e) => setApiVersion(e.target.value)}
                                                />
                                                <span className="helper-text">For example: v19.0, v20.0, etc.</span>
                                            </div>

                                            <div className="form-group">
                                                <label>Page Access Token</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Page Access Token"
                                                    value={pageAccessToken}
                                                    onChange={(e) => setPageAccessToken(e.target.value)}
                                                />
                                                <span className="helper-text">Used for Page-level actions</span>
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>User Access Token</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter User Access Token"
                                                    value={userAccessToken}
                                                    onChange={(e) => setUserAccessToken(e.target.value)}
                                                />
                                                <span className="helper-text">Used for User-level actions</span>
                                            </div>
                                        </>
                                    )}

                                    {!showAdAccount ? null : (
                                        <>
                                            <h3>Advertisement Account Information</h3>
                                            <p className="form-description">This gives access to your Facebook advertisement account's post and comments</p>
                                            
                                            <div className="form-group">
                                                <label>Advertisement Group ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="1234567890123456"
                                                    value={adGroupId}
                                                    onChange={(e) => setAdGroupId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Advertisement Account ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="9876543210987654"
                                                    value={adAccountId}
                                                    onChange={(e) => setAdAccountId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>AD ID</label>
                                                <input
                                                    type="text"
                                                    placeholder="1122334455667788"
                                                    value={adId}
                                                    onChange={(e) => setAdId(e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Identity Type</label>
                                                <input
                                                    type="text"
                                                    placeholder="Advertiser"
                                                    value={identityType}
                                                    onChange={(e) => setIdentityType(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="form-actions">
                                        {status && <span style={{ marginRight: '1rem', alignSelf: 'center', color: status.includes('Error') ? 'red' : 'green' }}>{status}</span>}
                                        <button className="btn-outline" onClick={() => setShowFacebookForm(false)}>Cancel</button>
                                        <button className="btn-primary" onClick={() => handleSubmit('FACEBOOK')}>Submit</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showTwitterForm && (
                            <div>
                                <button
                                    onClick={() => setShowTwitterForm(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: colors.grayLight,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                        color: colors.textSecondary,
                                        cursor: 'pointer',
                                        marginBottom: '1.5rem'
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Back to Accounts
                                </button>

                                {/* Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: `1px solid ${colors.border}`
                                }}>
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #333333 0%, #000000 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getPlatformIcon('TWITTER', 22)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.text, margin: 0 }}>X Account Connection</h3>
                                        <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: 0 }}>Enter your API credentials to enable X sync</p>
                                    </div>
                                </div>

                                <div style={{ maxWidth: '600px' }}>
                                    {/* Instructions */}
                                    <div style={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '1.5rem',
                                        backgroundColor: '#f3f4f6'
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#111827' }}>How to get X API Credentials</h4>
                                        <ol style={{ margin: '0', paddingLeft: '1.25rem', color: colors.textSecondary, fontSize: '0.9rem', lineHeight: '1.8' }}>
                                            <li>Go to <a href="https://developer.x.com" target="_blank" rel="noopener noreferrer" style={{ color: '#000000', fontWeight: '500' }}>developer.x.com</a></li>
                                            <li>Create a new project and app</li>
                                            <li>Get API Key, API Secret, and Access Tokens from "Keys and tokens"</li>
                                            <li>Enable OAuth 1.0a and use the credentials here</li>
                                        </ol>
                                    </div>

                                    {/* Step 1: OAuth Authorization */}
                                    <div style={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '1.5rem',
                                        backgroundColor: colors.grayLight
                                    }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Step 1: Authorize with X</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            Click the button below to authorize this application with your X account. You'll be redirected to grant permissions.
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(`${API_URL}/api/v1/auth/twitter/auth-url`);
                                                    const data = await response.json();

                                                    if (!data.auth_url) {
                                                        setStatus('Failed to get authorization URL');
                                                        return;
                                                    }

                                                    const width = 600;
                                                    const height = 700;
                                                    const left = window.screenX + (window.outerWidth - width) / 2;
                                                    const top = window.screenY + (window.outerHeight - height) / 2;
                                                    const popup = window.open(
                                                        data.auth_url,
                                                        'Twitter Authorization',
                                                        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
                                                    );

                                                    if (!popup) {
                                                        setStatus('Popup blocked. Please allow popups and try again.');
                                                        return;
                                                    }

                                                    const handleMessage = (event: MessageEvent) => {
                                                        if (event.data?.type === 'TWITTER_AUTH_SUCCESS') {
                                                            const data = event.data.data;
                                                            setTwitterClientId(data.clientId || '');
                                                            if (data.accessToken) setTwitterAccessToken(data.accessToken);
                                                            if (data.accessTokenSecret) setTwitterAccessTokenSecret(data.accessTokenSecret);
                                                            if (data.userId) setTwitterUserId(data.userId);

                                                            setStatus('Twitter authorized successfully! Fields auto-populated.');
                                                            fetchTokens();
                                                            window.removeEventListener('message', handleMessage);
                                                        } else if (event.data?.type === 'TWITTER_AUTH_ERROR') {
                                                            setStatus(`Authorization failed: ${event.data.description || event.data.error}`);
                                                            window.removeEventListener('message', handleMessage);
                                                        }
                                                    };
                                                    window.addEventListener('message', handleMessage);
                                                } catch (error) {
                                                    setStatus('Failed to initiate Twitter authorization');
                                                    console.error('Twitter auth error:', error);
                                                }
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '0.875rem 1.5rem',
                                                backgroundColor: '#000000',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '0.5rem',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#000000'}
                                        >
                                            🔐 Authorize with X
                                        </button>
                                    </div>

                                    {/* Twitter API Credentials Form */}
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600' }}>Standard Account Information</h4>
                                        <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary, fontSize: '0.9rem' }}>
                                            Review or manually adjust the keys if needed.
                                        </p>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Twitter Username (Handle)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., @yourusername"
                                                value={twitterUsername}
                                                onChange={(e) => setTwitterUsername(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Twitter Numeric User ID
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., 123456789"
                                                value={twitterUserId}
                                                onChange={(e) => setTwitterUserId(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                API Key (Consumer Key)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter your Twitter API Key"
                                                value={twitterApiKey}
                                                onChange={(e) => setTwitterApiKey(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                API Secret (Consumer Secret)
                                            </label>
                                            <input
                                                type="password"
                                                placeholder="Enter your Twitter API Secret"
                                                value={twitterApiSecret}
                                                onChange={(e) => setTwitterApiSecret(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Access Token *
                                            </label>
                                            <input
                                                type="password"
                                                placeholder="Enter your Twitter Access Token"
                                                value={twitterAccessToken}
                                                onChange={(e) => setTwitterAccessToken(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Access Token Secret *
                                            </label>
                                            <input
                                                type="password"
                                                placeholder="Enter your Twitter Access Token Secret"
                                                value={twitterAccessTokenSecret}
                                                onChange={(e) => setTwitterAccessTokenSecret(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Refresh Token (OAuth 2.0)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter your Twitter Refresh Token"
                                                value={twitterBearerToken}
                                                onChange={(e) => setTwitterBearerToken(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Client ID (OAuth 2.0 - Optional)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter your Twitter Client ID (optional)"
                                                value={twitterClientId}
                                                onChange={(e) => setTwitterClientId(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                                                Client Secret (OAuth 2.0 - Optional)
                                            </label>
                                            <input
                                                type="password"
                                                placeholder="Enter your Twitter Client Secret (optional)"
                                                value={twitterClientSecret}
                                                onChange={(e) => setTwitterClientSecret(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Status Message */}
                                    {status && (
                                        <div style={{
                                            padding: '0.75rem 1rem',
                                            marginBottom: '1rem',
                                            borderRadius: '8px',
                                            backgroundColor: status.includes('success') ? colors.successLight : status.includes('Error') ? colors.dangerLight : colors.blueLight,
                                            color: status.includes('success') ? colors.success : status.includes('Error') ? colors.danger : colors.primary,
                                            fontSize: '0.9rem'
                                        }}>
                                            {status}
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowTwitterForm(false)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                fontSize: '0.95rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => handleSubmit('TWITTER')}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                background: 'linear-gradient(135deg, #333333 0%, #000000 100%)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.95rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                                            }}
                                        >
                                            Save X Account
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showDeleteModal && (
                            <div style={{
                                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', zIndex: 1000
                            }}>
                                <div style={{
                                    backgroundColor: 'white', borderRadius: '16px',
                                    maxWidth: '450px', width: '90%', padding: '1.5rem',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div style={{ padding: '0.625rem', backgroundColor: colors.dangerLight, borderRadius: '10px' }}>
                                            <AlertTriangle size={22} color={colors.danger} />
                                        </div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: colors.danger, margin: 0 }}>Delete Account</h3>
                                    </div>
                                    <p style={{ margin: '0 0 1rem 0', lineHeight: '1.6', color: colors.textSecondary, fontSize: '0.95rem' }}>
                                        Are you sure you want to delete this account? This action cannot be undone and will stop all data synchronization.
                                    </p>
                                    <p style={{ marginBottom: '1rem' }}>
                                        To confirm, type <strong style={{ color: colors.danger }}>delete</strong> below:
                                    </p>
                                    <input
                                        type="text"
                                        placeholder="Type 'delete' to confirm"
                                        value={deleteConfirmationInput}
                                        onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            marginBottom: '1.25rem',
                                            border: `2px solid ${deleteConfirmationInput.toLowerCase() === 'delete' ? colors.danger : colors.border}`,
                                            borderRadius: '10px',
                                            fontSize: '0.95rem',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setShowDeleteModal(false)}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                backgroundColor: 'white',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '500',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmDelete}
                                            disabled={deleteConfirmationInput.toLowerCase() !== 'delete'}
                                            style={{
                                                padding: '0.625rem 1.25rem',
                                                background: deleteConfirmationInput.toLowerCase() === 'delete'
                                                    ? `linear-gradient(135deg, ${colors.danger} 0%, #dc2626 100%)`
                                                    : '#fca5a5',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                cursor: deleteConfirmationInput.toLowerCase() === 'delete' ? 'pointer' : 'not-allowed',
                                                boxShadow: deleteConfirmationInput.toLowerCase() === 'delete' ? '0 2px 6px rgba(239,68,68,0.3)' : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.375rem'
                                            }}
                                        >
                                            <Trash2 size={14} />
                                            Delete Account
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;
